import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

import { View, Text } from "../../components/base";
import { useAppDispatch, useAppSelector } from "../../redux";
import { agentRecipeActions, AgentRecipeEnvironment } from "../../redux/reducers/agentRecipeSlice";
import { Icon } from "../../components/base/Icon";
import { AgentFunctionEditor } from "../../components/agents/AgentFunctionEditor";
import { Tabs, Tab } from "../../components/base/Tabs";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

export const AgentRecipeDetailsPage = () => {
  const dispatch = useAppDispatch();
  const { agentRecipeId } = useParams<{ agentRecipeId: string }>();
  const agentRecipe = useAppSelector((state) => (agentRecipeId ? state.agentRecipe.entities[agentRecipeId] : null));
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Parse environments safely
  const agentEnvironments: AgentRecipeEnvironment[] | null = useMemo(() => {
    if (!agentRecipe) {
      return null;
    }
    if (Array.isArray(agentRecipe.environments)) {
      return agentRecipe.environments;
    }
    return null;
  }, [agentRecipe]);

  // Create tabs for the Tabs component
  const tabs: Tab[] = useMemo(() => {
    return agentEnvironments?.map((env) => ({ id: env.name, label: env.name })) ?? [];
  }, [agentEnvironments]);

  // Set initial active tab when environments load
  useEffect(() => {
    if (!activeTab && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
    // Reset active tab if environments disappear or the current one is no longer valid
    if (activeTab && !tabs.find((tab) => tab.id === activeTab)) {
      setActiveTab(tabs.length > 0 ? tabs[0].id : null);
    }
  }, [tabs, activeTab]);

  // Find the active environment based on the activeTab state
  const activeEnvironment = useMemo(() => {
    return agentEnvironments?.find((env) => env.name === activeTab) ?? null;
  }, [agentEnvironments, activeTab]);

  // Determine language from the *active* environment, default to python
  const language = useMemo(() => {
    return activeEnvironment?.functionLanguage || "python";
  }, [activeEnvironment]);

  // Get function code from the *active* environment
  const functionCode = useMemo(() => {
    return activeEnvironment?.functionCode || "# Select an environment tab to view its code.";
  }, [activeEnvironment]);

  useEffect(() => {
    if (agentRecipeId) {
      dispatch(agentRecipeActions.getById({ id: agentRecipeId }));
    }
  }, [agentRecipeId, dispatch]);

  if (!agentRecipe) {
    return (
      <View className="flex-1 justify-center items-center">
        <Text>Loading recipe...</Text>
      </View>
    );
  }

  // Define placeholder text here, outside the return statement
  const placeholderText =
    agentEnvironments && agentEnvironments.length > 0 ? "Select an environment tab to view details." : "No environments defined in this recipe.";

  return (
    <View className="relative flex flex-row h-full overflow-hidden">
      {/* Left Side: Environments and Code */}
      <View className="w-2/3 overflow-y-auto bg-gray-50 dark:bg-gray-900/60 flex flex-col min-h-0">
        <motion.div
          className="flex flex-col flex-1 min-h-0" // Ensure motion div is flexible
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Tabs Navigation */}
          <Tabs
            tabs={tabs}
            activeTab={activeTab || ""}
            onChange={setActiveTab}
            className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900/60 border-b border-gray-200 dark:border-gray-700 px-4" // Make tabs sticky, add padding
          />

          {/* Tab Content Area */}
          <View className="flex flex-col flex-1 min-h-0 gap-4 p-4">
            {" "}
            {/* Add padding and flex properties */}
            {activeEnvironment ? (
              <>
                {/* Grid for Variables and Language */}
                <View className="grid grid-cols-3 gap-4">
                  {/* Required Variables Card */}
                  <View className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm col-span-2">
                    <Text className="text-base font-semibold mb-3 text-gray-700 dark:text-gray-300">Required Variables</Text>
                    <View>
                      {activeEnvironment.variables && activeEnvironment.variables.length > 0 ? (
                        // Use map for better formatting potential later
                        activeEnvironment.variables.map((variable) => (
                          <Text
                            key={variable}
                            className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mr-2 mb-2 inline-block"
                          >
                            {variable}
                          </Text>
                        ))
                      ) : (
                        <Text className="text-sm text-gray-400 dark:text-gray-500 italic">None required</Text>
                      )}
                    </View>
                  </View>

                  {/* Language Card */}
                  <View className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm">
                    <Text className="text-base font-semibold mb-3 text-gray-700 dark:text-gray-300">Language</Text>
                    <View className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 mt-1 py-2 rounded-md w-fit">
                      <Icon
                        name={language === "python" ? "python" : "javascript"}
                        size={16}
                      />
                      <Text className="capitalize text-gray-700 dark:text-gray-300">{language}</Text>
                    </View>
                  </View>
                </View>

                {/* Function Code Card */}
                <View className="pb-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col flex-1 min-h-0">
                  <Text className="ml-4 mt-2 text-base font-semibold mb-2 text-gray-700 dark:text-gray-300">Function Code</Text>
                  <AgentFunctionEditor
                    value={functionCode}
                    language={language}
                    readOnly={true}
                    onChange={() => {}}
                  />
                </View>
              </>
            ) : (
              <View className="flex-1 justify-center items-center p-4">
                <Text className="text-gray-500 dark:text-gray-400">
                  {placeholderText} {/* Use the pre-defined variable */}
                </Text>
              </View>
            )}
          </View>
        </motion.div>
      </View>

      {/* Right Side: Recipe Details */}
      <View className="w-1/3 px-4 py-6 overflow-y-auto border-l border-gray-200 dark:border-gray-700">
        <motion.div
          className="flex flex-col flex-1 gap-6"
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Recipe Name Display */}
          <View>
            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Recipe Name </Text>
            <View className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 mt-2 py-2 rounded-md w-fit">
              <Text className="text-lg font-semibold text-gray-800 dark:text-gray-200">{agentRecipe.name}</Text>
            </View>
          </View>

          {/* Recipe Description Display */}
          <View>
            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Recipe Description</Text>
            <View className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 mt-2 py-2 rounded-md w-fit">
              <Text className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{agentRecipe.description}</Text>
            </View>
          </View>
        </motion.div>
      </View>
    </View>
  );
};
